import { executeContentAction, updateContent } from '@/api/content-api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { contentKeys, useContentDetail } from '@/hooks/useContentQueries';
import { cn } from '@/lib/utils';
import { useAsyncStore } from '@/store/asyncStore';
import { getAxiosErrorDisplayMessage } from '@/utils/error-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';

export interface ChatTitleBarProps {
  neoChatId?: number;
  busy?: boolean;
  /** Increment when the user starts a fresh session (e.g. toolbar New Chat) to clear pre-chat title drafts. */
  sessionEpoch: number;
}

export function ChatTitleBar({ neoChatId, busy, sessionEpoch }: ChatTitleBarProps) {
  const queryClient = useQueryClient();
  const { addTask, updateTask } = useAsyncStore();
  const detailQuery = useContentDetail(neoChatId ?? 0, { enabled: !!neoChatId });
  const [editOpen, setEditOpen] = useState(false);
  const [draftTitle, setDraftTitle] = useState('');
  const [draftDescription, setDraftDescription] = useState('');
  const [localTitle, setLocalTitle] = useState('');
  const [localDescription, setLocalDescription] = useState('');
  const pendingPersistRef = useRef<{ title: string; description: string } | null>(null);

  const content = detailQuery.data;
  const title = content?.title?.trim() ?? '';
  const description = content?.description?.trim() ?? '';

  useEffect(() => {
    setLocalTitle('');
    setLocalDescription('');
    pendingPersistRef.current = null;
  }, [sessionEpoch]);

  useEffect(() => {
    if (!editOpen) {
      return;
    }
    if (neoChatId) {
      setDraftTitle(title);
      setDraftDescription(description);
    } else {
      setDraftTitle(localTitle);
      setDraftDescription(localDescription);
    }
  }, [editOpen, neoChatId, title, description, localTitle, localDescription]);

  useEffect(() => {
    if (!neoChatId || pendingPersistRef.current === null) {
      return;
    }
    const payload = pendingPersistRef.current;
    pendingPersistRef.current = null;
    void updateContent(neoChatId, {
      contentType: 'NEO_CHAT',
      title: payload.title,
      description: payload.description,
    })
      .then((updated) => {
        queryClient.setQueryData(contentKeys.detail(updated.id), updated);
        setLocalTitle('');
        setLocalDescription('');
      })
      .catch(() => {
        pendingPersistRef.current = payload;
      });
  }, [neoChatId, queryClient]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!neoChatId) {
        throw new Error('No chat to update.');
      }
      return updateContent(neoChatId, {
        contentType: 'NEO_CHAT',
        title: draftTitle,
        description: draftDescription,
      });
    },
    onSuccess: (updated) => {
      queryClient.setQueryData(contentKeys.detail(updated.id), updated);
      setEditOpen(false);
    },
  });

  const savePreChatDraft = () => {
    setLocalTitle(draftTitle);
    setLocalDescription(draftDescription);
    pendingPersistRef.current = { title: draftTitle, description: draftDescription };
    setEditOpen(false);
  };

  const handleSaveClick = () => {
    if (neoChatId) {
      saveMutation.mutate();
      return;
    }
    savePreChatDraft();
  };

  const regenerateMutation = useMutation({
    mutationFn: async () => {
      if (!neoChatId) {
        return;
      }
      const taskId = addTask({
        title: 'Regenerating chat title',
        description: 'Re-running LLM title and description.',
        status: 'processing',
        contentType: 'NEO_CHAT',
        contentId: neoChatId,
      });
      try {
        await executeContentAction(neoChatId, 'regenerateNeoMetadata');
        updateTask(taskId, {
          status: 'completed',
          progress: 100,
          description: 'Title and description updated.',
        });
        await queryClient.invalidateQueries({ queryKey: contentKeys.detail(neoChatId) });
      } catch (error: unknown) {
        updateTask(taskId, {
          status: 'failed',
          description: getAxiosErrorDisplayMessage(error, 'Regeneration failed.'),
        });
        throw error;
      }
    },
  });

  const preChatLine =
    [localTitle.trim(), localDescription.trim()].filter(Boolean).join(' · ') || 'New Chat';

  const lineLabel = !neoChatId
    ? preChatLine
    : detailQuery.isLoading
      ? '…'
      : [title || 'Neo chat', description].filter(Boolean).join(' · ') || 'Neo chat';

  const regenerateDisabled = !neoChatId || busy || regenerateMutation.isPending;

  return (
    <div className="mt-1.5 flex items-center gap-0.5">
      <Popover open={editOpen} onOpenChange={setEditOpen}>
        <PopoverTrigger asChild>
          <button
            data-testid="speak2me-chat-meta-line"
            type="button"
            title="Edit title and description"
            className="min-h-0 min-w-0 flex-1 cursor-pointer truncate py-0 text-left text-xs leading-snug text-muted-foreground hover:text-foreground"
          >
            {lineLabel}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 space-y-3 p-3" align="start" side="bottom">
          <div className="space-y-1.5">
            <Label htmlFor="chat-title-bar-title" className="text-xs">
              Title
            </Label>
            <Input
              id="chat-title-bar-title"
              value={draftTitle}
              onChange={(e) => setDraftTitle(e.target.value)}
              className="h-8 text-sm"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="chat-title-bar-description" className="text-xs">
              Description
            </Label>
            <Textarea
              id="chat-title-bar-description"
              value={draftDescription}
              onChange={(e) => setDraftDescription(e.target.value)}
              rows={3}
              className="min-h-[4.5rem] resize-y text-sm"
            />
          </div>
          {saveMutation.isError && (
            <p className="text-xs text-destructive">
              {getAxiosErrorDisplayMessage(saveMutation.error, 'Could not save changes.')}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => setEditOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              size="sm"
              disabled={neoChatId ? saveMutation.isPending : false}
              onClick={handleSaveClick}
            >
              Save
            </Button>
          </div>
        </PopoverContent>
      </Popover>

      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="h-5 w-5 shrink-0"
        disabled={regenerateDisabled}
        title="Regenerate title and description from your first message"
        aria-label="Regenerate title and description"
        onClick={() => regenerateMutation.mutate()}
      >
        <RefreshCw
          className={cn('h-3 w-3 text-muted-foreground', regenerateMutation.isPending && 'animate-spin')}
        />
      </Button>
    </div>
  );
}
